import { useState } from 'react';

import { useOrganizations } from '../../hooks/useOrganizations';

import Styles from './styles.module.scss';
import { UsersListView } from './UsersListView';

import { useDebouncedValue } from '@/app/hooks/useDebounce';
import { SearchIcon } from '@/components/Elements/Icons';
import { SmartSelect } from '@/components/SmartSelect';
import { useUsersInfiniteQuery } from '@/features/Shared/users/api';

interface UsersListPickerProps {
	value?: string[];
	onChange?: (value: string[]) => void;
}

export const UsersListPicker = ({ value, onChange }: UsersListPickerProps) => {
	const [selectedOrganization, setSelectedOrganization] = useState<string | undefined>(undefined);
	const [searchTerm, setSearchTerm] = useState('');
	const debouncedSearchTerm = useDebouncedValue(searchTerm, 600);
	const { organizations } = useOrganizations();
	const { currentData, isLoading, isFetching, fetchNextPage } = useUsersInfiniteQuery({
		pageSize: 100,
		name: debouncedSearchTerm,
		organizationIds: selectedOrganization
			? [selectedOrganization]
			: organizations?.map((org) => org.id),
	});
	const hasMore = currentData?.pages?.[currentData.pages.length - 1]?.moreResults ?? false;
	const items = currentData?.pages?.flatMap((page) => page.items) ?? [];

	const sortedItems = items.sort((a, b) => {
		const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
		const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
		return nameA.localeCompare(nameB);
	});

	const isLoadingData = isLoading || (!currentData && isFetching);

	return (
		<div className={Styles.usersBody}>
			<div className={Styles.header}>
				<p>Select Organization</p>
				<SmartSelect value={selectedOrganization} onChange={setSelectedOrganization}>
					<SmartSelect.Option value={undefined}>All Organizations</SmartSelect.Option>
					{organizations?.map((org) => (
						<SmartSelect.Option key={org.id} value={org.id}>
							{org.name}
						</SmartSelect.Option>
					))}
				</SmartSelect>
				<p>Select Group or Group Member</p>
				<div className={Styles.searchInputContainer}>
					<SearchIcon />
					<input
						type="text"
						placeholder="Search Users"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
					/>
				</div>
			</div>
			<UsersListView
				isLoading={isLoadingData}
				data={sortedItems}
				fetchMore={hasMore ? fetchNextPage : undefined}
				value={value}
				onChange={onChange}
			/>
		</div>
	);
};
